import {
    defineElement,
    FC,
    onDidLoad,
    useBindMethod,
    useElement,
    useEvent,
    useListen,
} from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { setButtonLabel } from '@atoms/button/utilities';
import { debounce } from '@bpd-library/utilities';
import {
    resetAreaSubscriptionState,
    toggleProjectPhaseSelected,
    updateAreaSubscriptionState,
} from '@features/area-subscription/store';
import {
    AreaSubscriptionFooterElement,
    AreaSubscriptionInitializer,
    AreaSubscriptionModalElement,
    AreaSubscriptionModalUpdateAction,
    ExtendedAreaSubscriptionState,
} from '@features/area-subscription/types';
import {
    getFooterDataForStatus,
    updateAreaSubscriptionTranslationOptions,
} from '@features/area-subscription/utilities';
import { isSubscribedToProjectPhase, subscriptionStore } from '@store/subscription/store';
import { useAreaData, useStylesheet } from '@utilities/hooks';

import style from './area-subscription.scss';
import { AREA_SUBSCRIPTION_LOADED_EVENT } from './constants';

const INVISIBLE_CLASS = 'u-sr-only';

const AreaSubscription: FC<AreaSubscriptionFooterElement> = ({ element }) => {
    useStylesheet(style);

    useElement<HTMLParagraphElement>('[js-hook-area-subscription-footer-title]', {
        as: 'footerTitle',
    });
    useElement<HTMLParagraphElement>('[js-hook-area-subscription-footer-subtitle]', {
        as: 'footerSubtitle',
    });
    const footerCta = useElement<HTMLButtonElement>('[js-hook-area-subscription-footer-cta]', {
        as: 'footerCta',
    });

    useElement<AreaSubscriptionModalElement>('area-subscription-modal', {
        as: 'modal',
    });

    useListen(footerCta, 'click', () => element.modal.openModal('footer'));

    const onLoaded = useEvent<{ loaded: boolean }>({ eventName: AREA_SUBSCRIPTION_LOADED_EVENT });
    onDidLoad(async () => {
        const { areaProject } = await useAreaData();

        if (areaProject.status === 'Sold') {
            onLoaded.emit({ loaded: false });
            return;
        }

        if (!element.modal.connected) await element.modal.componentOnReady();
        await updateData();
        onLoaded.emit({ loaded: true });
    });

    subscriptionStore.subscribe(
        debounce(async () => await updateData({ modalActiveAction: 'submit' }), 500),
        ['areaProject', 'projectPhases', 'propertyTypes'],
    );

    subscriptionStore.subscribe(
        async () => await updateData({ modalActiveAction: 'login' }),
        ['isUpdatedFromUserStore'],
    );

    async function updateData(options?: { modalActiveAction?: AreaSubscriptionModalUpdateAction }) {
        if (options?.modalActiveAction === 'close') resetAreaSubscriptionState();

        const state = await updateAreaSubscriptionState();
        await updateAreaSubscriptionTranslationOptions(state);

        await updateFooterData(state);
        await updateModalData(state, options);
    }

    async function updateFooterData(state: ExtendedAreaSubscriptionState) {
        const { status } = state;
        if (!status) return;

        if (
            (status === 'subscribedToAreaNoProjectsAvailable' ||
                status === 'subscribedToAreaSubscribedToAllProjects') &&
            !element.modal.modal.active
        ) {
            element.classList.add(INVISIBLE_CLASS);
        } else {
            element.classList.remove(INVISIBLE_CLASS);
        }

        const footerData = await getFooterDataForStatus(status);
        if (!footerData) return;

        footerData.footerTitle && (element.footerTitle.innerText = footerData.footerTitle);
        footerData.footerSubtitle && (element.footerSubtitle.innerHTML = footerData.footerSubtitle);
        footerData.footerCta && setButtonLabel(element.footerCta, footerData.footerCta);
    }

    async function updateModalData(
        state: ExtendedAreaSubscriptionState,
        options?: { modalActiveAction?: AreaSubscriptionModalUpdateAction },
    ) {
        if (element.modal.modal.active) {
            await element.modal.updateModalActive(state, options);
        } else {
            await element.modal.updateModalInactive(state);
        }
    }

    const openInitialized: AreaSubscriptionInitializer = async (options) => {
        let selectedSubscription = options.areaOnly || false;
        options.selectedProjectPhases?.forEach((projectId) => {
            if (isSubscribedToProjectPhase(projectId)) return;
            toggleProjectPhaseSelected(projectId, true);
            selectedSubscription = true;
        });

        await element.modal.openModal(options.source!);
        if (selectedSubscription) await element.modal.nextStepClick();
    };

    useBindMethod('openInitialized', openInitialized);
    const onModalClose = () => updateData({ modalActiveAction: 'close' });
    return <area-subscription-modal onModalClose={onModalClose} />;
};

defineElement('bpd-area-subscription', AreaSubscription);
